import { I18n } from '@/i18n/I18n';
import { useEffect, useState, useRef, useMemo } from 'react';
import { List } from '../SVGs/List';
import { Grid } from '../SVGs/Grid';
import { Search } from 'lucide-react';

type ObjectWithTitle = {
  title: string;
};

interface MyComponentProps<T> {
  items: T[];
  setItems: (items: T[]) => void;
  modelLabel: string;
  showViewStyle?: boolean;
  viewStyle?: 'list' | 'grid';
  setViewStyle?: (viewStyle: 'list' | 'grid') => void;
  showItemCount?: boolean;
  children?: React.ReactNode;
}

export function SearchList<T extends ObjectWithTitle>(props: MyComponentProps<T>) {
  const { items, setItems, modelLabel, showViewStyle = false, viewStyle = 'list', setViewStyle = () => {}, children, showItemCount = true } = props;

  const [searchTerm, setSearchTerm] = useState('');
  const lastItemsKeyRef = useRef<string>('');
  const lastSearchTermRef = useRef('');

  const itemsKey = useMemo(() => {
    return items.map(item => item.title).join('|');
  }, [items]);

  const filteredList = useMemo(() => {
    if (searchTerm === '') {
      return items;
    }
    return items.filter((item) => {
      return item.title.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [items, searchTerm]);

  useEffect(() => {
    const searchTermChanged = searchTerm !== lastSearchTermRef.current;
    const itemsChanged = itemsKey !== lastItemsKeyRef.current;

    if (searchTermChanged || itemsChanged) {
      lastSearchTermRef.current = searchTerm;
      lastItemsKeyRef.current = itemsKey;
      setItems(filteredList);
    }
  }, [searchTerm, itemsKey, filteredList, setItems]);

  return (
    <div className='d-flex justify-content-between'>
      <div className='d-flex gap-2 me-1 flex-grow-1'>
        <div className="position-relative mt-2 mb-2 w-25">
          <Search size={16} className="position-absolute top-50 translate-middle-y" style={{ left: '12px', color: 'var(--neutral-500)' }} />
          <input
            type="text"
            className="form-control form-control-neutral form-control-pill"
            style={{ paddingLeft: '36px' }}
            aria-label={I18n.get('findAModel', { model: modelLabel })}
            placeholder={I18n.get('findAModel', { model: modelLabel })}
            onChange={(e) => (setSearchTerm(e.target.value))}
          />
        </div>
       { showItemCount && <> 
          <span className='border-end' />
          <p className='my-auto'>
            {I18n.get('showingThisManyModels', { shown: filteredList.length.toString(), total: items.length.toString(), model: modelLabel})}
          </p>
          <span className='border-end' />
        </>
       }
      </div>
      { (showViewStyle || children) &&
        <div>
          { showViewStyle &&
            <div className="btn-group border my-auto align-self-end" role="group" aria-label={I18n.get('viewStyle')}>
              <button className={`btn btn-sm ${viewStyle === 'grid' ? 'btn-primary' : 'btn-primary-subtle'}`} onClick={() => setViewStyle('grid')} aria-label={I18n.get('grid')}><Grid /></button>
              <button className={`btn btn-sm ${viewStyle === 'list' ? 'btn-primary' : 'btn-primary-subtle'}`} onClick={() => setViewStyle('list')} aria-label={I18n.get('list')}><List /></button>
            </div>
          }
          { children }
        </div>
      }
    </div>
  );
}
